import { useState, type FormEvent } from 'react';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Label } from '../ui/Label.js';
import { ApiError, apiGet, apiPost } from '../../lib/api.js';

type LookedUpUser = {
  id: string;
  email: string;
  full_name: string;
  role: string;
  agency_name: string | null;
  two_factor_enabled: boolean;
};

const GREEN = { backgroundColor: '#E8F3EC', color: '#1E7A47', border: '1px solid #C9E5D2' };
const GREY = { backgroundColor: '#F0EEE8', color: '#6B6B6B', border: '1px solid #E0DBD0' };
const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super admin',
  admin: 'Agency admin',
  consultant: 'Consultant',
};

/**
 * Super-admin "Users" tool: look a user up by email and, if they're locked out of
 * 2FA (lost phone + recovery codes), reset it. No full user-management section —
 * just the lookup + reset escape hatch.
 */
export function AdminUsersPanel() {
  const [email, setEmail] = useState('');
  const [user, setUser] = useState<LookedUpUser | null>(null);
  const [looking, setLooking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState<string | null>(null);

  async function lookup(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setUser(null);
    setResetDone(null);
    setConfirming(false);
    setLooking(true);
    try {
      const res = await apiGet<{ user: LookedUpUser }>(
        `/admin/users/lookup?email=${encodeURIComponent(email.trim())}`,
      );
      setUser(res.user);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setLooking(false);
    }
  }

  async function reset() {
    if (!user) return;
    setError(null);
    setResetting(true);
    try {
      const res = await apiPost<{ ok: true; email: string; was_enabled: boolean }>(
        `/admin/users/${user.id}/reset-2fa`,
      );
      setUser({ ...user, two_factor_enabled: false });
      setConfirming(false);
      setResetDone(res.email);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setResetting(false);
    }
  }

  return (
    <div className="max-w-xl">
      <form onSubmit={lookup} className="surface p-5 sm:p-6 flex flex-col gap-3">
        <div>
          <Label htmlFor="lookup-email">Find a user by email</Label>
          <Input
            id="lookup-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@agency.com.au"
          />
        </div>
        <Button type="submit" loading={looking} className="self-start">
          Look up
        </Button>
      </form>

      {error && (
        <div className="mt-4 rounded-lg bg-danger-bg border border-danger/20 px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      {user && (
        <div className="surface p-5 sm:p-6 mt-4">
          <div className="font-medium text-ink">{user.full_name}</div>
          <div className="text-sm text-muted">{user.email}</div>
          <div className="text-sm text-muted mt-1">
            {ROLE_LABEL[user.role] ?? user.role}
            {user.agency_name ? ` · ${user.agency_name}` : ''}
          </div>

          <div className="mt-4">
            <span
              className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
              style={user.two_factor_enabled ? GREEN : GREY}
            >
              {user.two_factor_enabled ? 'Two-factor on' : 'Two-factor off'}
            </span>
          </div>

          {resetDone ? (
            <p className="mt-4 text-sm text-muted">
              Two-factor reset. We&rsquo;ve notified <span className="font-medium">{resetDone}</span>.
              They can now sign in with just their password.
            </p>
          ) : user.two_factor_enabled ? (
            confirming ? (
              <div className="mt-4 flex flex-col gap-2">
                <span className="text-sm text-ink max-w-md">
                  Reset 2FA for <span className="font-medium">{user.email}</span>? They&rsquo;ll sign
                  in with just their password, and we&rsquo;ll email them.
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="danger" size="sm" loading={resetting} onClick={reset}>
                    Reset 2FA
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={resetting}
                    onClick={() => setConfirming(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="secondary" size="sm" className="mt-4" onClick={() => setConfirming(true)}>
                Reset 2FA
              </Button>
            )
          ) : (
            <p className="mt-4 text-sm text-muted">
              This user doesn&rsquo;t have two-factor enabled — nothing to reset.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
